import type { MediaDetail, MediaSummary } from '../../shared/contracts'

export interface DetailCacheEntry {
    data: MediaDetail
    updatedAt: number
}

const MAX_DETAIL_ENTRIES = 16
const MAX_SUMMARY_ENTRIES = 120
const summaries = new Map<string, MediaSummary>()
const details = new Map<string, DetailCacheEntry>()

export function mediaCacheKey(sourceId: string, id: string) {
    return `${sourceId}:${id}`
}

export function rememberMediaSummary(media: MediaSummary) {
    const key = mediaCacheKey(media.sourceId, media.id)
    summaries.delete(key)
    summaries.set(key, media)
    while (summaries.size > MAX_SUMMARY_ENTRIES) {
        const oldest = summaries.keys().next().value
        if (!oldest) { break }
        summaries.delete(oldest)
    }
}

export function rememberMediaDetail(detail: MediaDetail) {
    const key = mediaCacheKey(detail.sourceId, detail.id)
    rememberMediaSummary(detail)
    details.delete(key)
    details.set(key, { data: detail, updatedAt: Date.now() })
    while (details.size > MAX_DETAIL_ENTRIES) {
        const oldest = details.keys().next().value
        if (!oldest) { break }
        details.delete(oldest)
    }
}

export function recallMediaDetail(sourceId: string, id: string): DetailCacheEntry | undefined {
    const key = mediaCacheKey(sourceId, id)
    const entry = details.get(key)
    if (!entry) { return undefined }
    details.delete(key)
    details.set(key, entry)
    return entry
}

export function recallFreshMediaDetail(sourceId: string, id: string, maxAge: number): DetailCacheEntry | undefined {
    const entry = recallMediaDetail(sourceId, id)
    return entry && Date.now() - entry.updatedAt <= maxAge ? entry : undefined
}

export function recallMediaPreview(sourceId: string, id: string): MediaDetail | undefined {
    const cached = recallMediaDetail(sourceId, id)
    if (cached) { return { ...cached.data, alternatives: undefined, episodes: [] } }
    const summary = summaries.get(mediaCacheKey(sourceId, id))
    return summary ? { ...summary, episodes: [] } : undefined
}
